// Mock data shared across pages - represents the full xAPI backend surface
import React from 'react';

const CATEGORIES = ['All', 'AI Models', 'Sandbox', 'Social', 'Crypto', 'Data', 'Search', 'Media', 'Dev Tools'];

const APIS = [
  {
    id: 'llm-gateway', slug: 'llm', name: 'LLM Gateway', provider: 'xapi-core',
    category: 'AI Models', host: 'llm.p.xapi.to',
    description: 'Unified interface for OpenAI, Anthropic, Google and open-source models with token-based billing and streaming.',
    hits: 89_420_331, stars: 1284, uptime: 99.98, latency: 184,
    status: 'active', verified: true, billing: 'token',
    tags: ['openai', 'anthropic', 'streaming', 'token-billing'],
    endpoints: [
      { id: 'e1', method: 'POST', path: '/v1/chat/completions', name: 'Chat Completions', cost: '0.003 / 1k tok', desc: 'Generate text with streaming support across 40+ models.' },
      { id: 'e2', method: 'POST', path: '/v1/embeddings', name: 'Embeddings', cost: '0.0001 / 1k tok', desc: 'Generate vector embeddings for semantic search.' },
      { id: 'e3', method: 'POST', path: '/v1/images/generations', name: 'Image Generation', cost: '0.04 / image', desc: 'DALL-E 3, SD3, Flux models.' },
      { id: 'e4', method: 'GET', path: '/v1/models', name: 'List Models', cost: 'free', desc: 'Return all available models with pricing.' },
    ],
  },
  {
    id: 'twitter-data', slug: 'twitter', name: 'Twitter Data', provider: '@datalab',
    category: 'Social', host: 'twitter.p.xapi.to',
    description: 'Real-time Twitter/X data: user profiles, tweets, trends, and search with sub-second latency.',
    hits: 12_540_800, stars: 840, uptime: 99.92, latency: 320,
    status: 'active', verified: true, billing: 'per-call',
    tags: ['social', 'realtime', 'scraping'],
    endpoints: [
      { id: 't1', method: 'GET', path: '/v2/users/:id', name: 'Get User Profile', cost: '$0.0005', desc: 'Detailed user profile by ID or handle.' },
      { id: 't2', method: 'GET', path: '/v2/tweets/search', name: 'Search Tweets', cost: '$0.001', desc: 'Full-text tweet search with filters.' },
      { id: 't3', method: 'GET', path: '/v2/trends/:loc', name: 'Get Trends', cost: '$0.0005', desc: 'Trending topics by location.' },
    ],
  },
  {
    id: 'onchain-analytics', slug: 'chain', name: 'On-chain Analytics', provider: '@blockscout',
    category: 'Crypto', host: 'chain.p.xapi.to',
    description: 'Real-time blockchain data across Base, Ethereum, Solana and BSC. Track whale movements and DEX flows.',
    hits: 5_421_900, stars: 620, uptime: 99.94, latency: 210,
    status: 'active', verified: true, billing: 'per-call',
    tags: ['web3', 'base', 'solana', 'defi'],
    endpoints: [
      { id: 'c1', method: 'GET', path: '/v1/price/:address', name: 'Get Token Price', cost: '$0.0002', desc: 'Current price of any token on supported chains.' },
      { id: 'c2', method: 'GET', path: '/v1/wallet/:addr/tx', name: 'Wallet Transactions', cost: '$0.0004', desc: 'Full transaction history for a wallet.' },
      { id: 'c3', method: 'POST', path: '/v1/whales/watch', name: 'Whale Watch', cost: '$0.002', desc: 'Subscribe to whale movement alerts.' },
    ],
  },
  {
    id: 'search-web', slug: 'search', name: 'Web Search', provider: '@searchlabs',
    category: 'Search', host: 'search.p.xapi.to',
    description: 'Fresh web search results with citation-ready snippets, optimized for LLM agents.',
    hits: 23_120_400, stars: 510, uptime: 99.96, latency: 430,
    status: 'active', verified: false, billing: 'per-call',
    tags: ['search', 'agents', 'serp'],
    endpoints: [
      { id: 's1', method: 'GET', path: '/v1/search', name: 'Search', cost: '$0.005', desc: 'Web search with configurable count, region, time range.' },
      { id: 's2', method: 'GET', path: '/v1/news', name: 'News Search', cost: '$0.005', desc: 'Recent news articles ranked by relevance.' },
    ],
  },
  {
    id: 'weather-pro', slug: 'weather', name: 'Weather Pro', provider: '@meteo',
    category: 'Data', host: 'weather.p.xapi.to',
    description: 'Hyperlocal weather forecasts with 1km resolution, 14-day outlook, severe alerts.',
    hits: 9_800_100, stars: 290, uptime: 99.99, latency: 95,
    status: 'active', verified: false, billing: 'per-call',
    tags: ['weather', 'forecast', 'alerts'],
    endpoints: [
      { id: 'w1', method: 'GET', path: '/v1/forecast', name: 'Forecast', cost: '$0.0001', desc: '14-day forecast by lat/lng.' },
      { id: 'w2', method: 'GET', path: '/v1/alerts', name: 'Severe Alerts', cost: '$0.0002', desc: 'Active weather alerts in a region.' },
    ],
  },
  {
    id: 'images-pro', slug: 'image', name: 'Image AI', provider: '@pixlab',
    category: 'Media', host: 'image.p.xapi.to',
    description: 'Background removal, upscaling, face analysis, OCR — all in one endpoint.',
    hits: 3_210_700, stars: 340, uptime: 99.91, latency: 1_200,
    status: 'active', verified: false, billing: 'per-call',
    tags: ['vision', 'ocr', 'upscale'],
    endpoints: [
      { id: 'i1', method: 'POST', path: '/v1/bg-remove', name: 'Background Remove', cost: '$0.01', desc: 'Remove background from image.' },
      { id: 'i2', method: 'POST', path: '/v1/upscale', name: 'Upscale 4x', cost: '$0.02', desc: 'AI upscale with detail preservation.' },
      { id: 'i3', method: 'POST', path: '/v1/ocr', name: 'OCR', cost: '$0.005', desc: 'Extract text from image.' },
    ],
  },
  {
    id: 'geo-ip', slug: 'geoip', name: 'GeoIP Resolver', provider: 'xapi-core',
    category: 'Data', host: 'geoip.p.xapi.to',
    description: 'Accurate IP geolocation, ASN, VPN/Tor detection, carrier lookup.',
    hits: 41_200_000, stars: 220, uptime: 99.99, latency: 45,
    status: 'active', verified: true, billing: 'per-call',
    tags: ['networking', 'geo', 'abuse'],
    endpoints: [
      { id: 'g1', method: 'GET', path: '/v1/ip/:addr', name: 'Lookup IP', cost: '$0.00005', desc: 'Geo, ASN, abuse signals.' },
    ],
  },
  {
    id: 'stripe-proxy', slug: 'payments', name: 'Payments Toolkit', provider: 'xapi-core',
    category: 'Dev Tools', host: 'pay.p.xapi.to',
    description: 'Unified payments: Stripe, Solana Pay, x402 link generation and webhook verification.',
    hits: 7_900_300, stars: 410, uptime: 99.97, latency: 180,
    status: 'active', verified: true, billing: 'per-call',
    tags: ['payments', 'stripe', 'x402'],
    endpoints: [
      { id: 'p1', method: 'POST', path: '/v1/links', name: 'Create Payment Link', cost: '$0.0005', desc: 'Multi-rail payment link.' },
      { id: 'p2', method: 'POST', path: '/v1/webhooks/verify', name: 'Verify Webhook', cost: 'free', desc: 'Verify webhook signatures.' },
    ],
  },
  {
    id: 'translate', slug: 'translate', name: 'Translate', provider: '@lingua',
    category: 'AI Models', host: 'translate.p.xapi.to',
    description: 'Neural machine translation across 120 languages with glossary support.',
    hits: 2_100_000, stars: 180, uptime: 99.94, latency: 220,
    status: 'active', verified: false, billing: 'token',
    tags: ['translation', 'nmt'],
    endpoints: [
      { id: 'tr1', method: 'POST', path: '/v1/translate', name: 'Translate Text', cost: '$0.00002 / char', desc: 'Detect + translate to target language.' },
    ],
  },
];

// Synthetic time-series
const sparkSeries = (seed = 1, len = 30, base = 100, amp = 30) => {
  let r = seed; const out = [];
  for (let i = 0; i < len; i++) {
    r = (r * 9301 + 49297) % 233280;
    out.push(base + Math.sin(i / 3 + seed) * amp * 0.6 + (r / 233280) * amp - amp/2);
  }
  return out;
};

const RECENT_CALLS = [
  { id: 'c001', ts: '2026-04-20 14:22:14', api: 'LLM Gateway', endpoint: 'POST /v1/chat/completions', status: 200, latency: 142, cost: 0.0041, key: 'sk-8f3a****a92f' },
  { id: 'c002', ts: '2026-04-20 14:22:09', api: 'LLM Gateway', endpoint: 'POST /v1/chat/completions', status: 200, latency: 190, cost: 0.0032, key: 'sk-8f3a****a92f' },
  { id: 'c003', ts: '2026-04-20 14:21:58', api: 'Twitter Data', endpoint: 'GET /v2/tweets/search', status: 200, latency: 310, cost: 0.001, key: 'sk-8f3a****a92f' },
  { id: 'c004', ts: '2026-04-20 14:21:43', api: 'On-chain Analytics', endpoint: 'GET /v1/price/:address', status: 200, latency: 85, cost: 0.0002, key: 'sk-2c41****b301' },
  { id: 'c005', ts: '2026-04-20 14:21:30', api: 'LLM Gateway', endpoint: 'POST /v1/chat/completions', status: 429, latency: 12, cost: 0, key: 'sk-8f3a****a92f' },
  { id: 'c006', ts: '2026-04-20 14:21:22', api: 'Web Search', endpoint: 'GET /v1/search', status: 200, latency: 420, cost: 0.005, key: 'sk-8f3a****a92f' },
  { id: 'c007', ts: '2026-04-20 14:21:15', api: 'LLM Gateway', endpoint: 'POST /v1/embeddings', status: 200, latency: 98, cost: 0.0001, key: 'sk-8f3a****a92f' },
  { id: 'c008', ts: '2026-04-20 14:20:58', api: 'GeoIP Resolver', endpoint: 'GET /v1/ip/:addr', status: 200, latency: 41, cost: 0.00005, key: 'sk-2c41****b301' },
  { id: 'c009', ts: '2026-04-20 14:20:41', api: 'LLM Gateway', endpoint: 'POST /v1/chat/completions', status: 500, latency: 2_410, cost: 0, key: 'sk-8f3a****a92f' },
  { id: 'c010', ts: '2026-04-20 14:20:22', api: 'Twitter Data', endpoint: 'GET /v2/users/:id', status: 200, latency: 280, cost: 0.0005, key: 'sk-8f3a****a92f' },
];

const API_KEYS = [
  { id: 'k1', name: 'Production', preview: 'sk-8f3a****a92f', created: '2026-02-14', lastUsed: '2s ago', usage: 18420, limit: 500, used: 284.12, sandbox: false, register: false, oauth: true, isDefault: true },
  { id: 'k2', name: 'Local dev',   preview: 'sk-2c41****b301', created: '2026-03-02', lastUsed: '3m ago', usage: 3201, limit: 50, used: 12.40, sandbox: true, register: false, oauth: false, isDefault: false },
  { id: 'k3', name: 'CI Runner',   preview: 'sk-1909****d4e2', created: '2026-04-01', lastUsed: '1h ago', usage: 921, limit: null, used: 5.12, sandbox: false, register: true, oauth: false, isDefault: false },
];

const MY_APIS = [
  { id: 'm1', name: 'Internal Crypto Feed', host: 'crypto-feed', status: 'ACTIVE', category: 'Crypto', version: 'v1.2.0', calls30d: 842_100, earnings30d: 1_240.55, endpoints: 6 },
  { id: 'm2', name: 'CMS Content API', host: 'cms', status: 'SANDBOX', category: 'Data', version: 'v0.9.0', calls30d: 1_200, earnings30d: 0, endpoints: 3 },
  { id: 'm3', name: 'Research Docs Search', host: 'docs-search', status: 'DRAFT', category: 'Search', version: '—', calls30d: 0, earnings30d: 0, endpoints: 2 },
];

const PENDING_REVIEWS = [
  { id: 'r1', name: 'Analytics Pro', provider: '@arya', host: 'analytics-pro', category: 'Data', status: 'PENDING_REVIEW', submitted: '2h ago', score: 86, errors: 0, warnings: 2 },
  { id: 'r2', name: 'Fast Embeddings', provider: '@xu', host: 'fast-emb', category: 'AI Models', status: 'PENDING_REVIEW', submitted: '5h ago', score: 92, errors: 0, warnings: 0 },
  { id: 'r3', name: 'SMS Gateway', provider: '@pulse', host: 'sms', category: 'Dev Tools', status: 'SANDBOX', submitted: '1d ago', score: 74, errors: 1, warnings: 3 },
  { id: 'r4', name: 'Crypto Sentinel', provider: '@vex', host: 'sentinel', category: 'Crypto', status: 'PENDING_REVIEW', submitted: '2d ago', score: 68, errors: 2, warnings: 1 },
];

// ============ AI Relay (中转站) — real per-upstream model pricing ============
// 数据来自生产环境 api_endpoints.tokenPricing 字段（实测从 prod snapshot 提取）。
// 每一行 = 某个上游中转支持的某个模型的真实定价（USD per 1M token）。
//
// 与之前的 mock 模型清单不同，这是**实际收费时使用的价格**。同一个 model name 在
// 不同上游下的价格可以差异巨大（如 claude-opus-4-6: SuperToken-逆向 $0.65/M vs
// OpenRouter $10/M），这正是 xAPI 价值所在 —— 帮用户在多个上游之间路由到最便宜的。
//
// 命名规则（与后端 catalog 对齐）：
//   - `model` 字段使用**规范化 canonical id**（全小写 + `.` → `-`）
//   - 同一 canonical 在不同上游下都用同一字符串（dedup key）
//
// Schema:
//   upstream:      string       —— 上游中转名（与 marketplace 的 service.name 对应）
//   model:         string       —— canonical id（lowercase + . → -）
//   canonical:     string       —— 人类友好的展示名
//   family:        string       —— 厂商家族（用于分类 pill）
//   in:            number       —— 输入价格 USD/1M tokens
//   out:           number       —— 输出价格 USD/1M tokens
//   note?:         string       —— 备注（如 "tier ≤200K"）
const AI_MODEL_PRICING = [
  // ===== SkyAPI =====
  { upstream:'SkyAPI', model:'gpt-5-4',                  canonical:'GPT-5.4',          family:'OpenAI',    in:0.50,  out:3.00 },
  { upstream:'SkyAPI', model:'claude-opus-4-7',          canonical:'Claude Opus 4.7',  family:'Anthropic', in:1.00,  out:5.00, note:'≤200K, 超出降阶' },
  { upstream:'SkyAPI', model:'claude-opus-4-6',          canonical:'Claude Opus 4.6',  family:'Anthropic', in:2.00,  out:7.50 },
  { upstream:'SkyAPI', model:'claude-sonnet-4-6',        canonical:'Claude Sonnet 4.6',family:'Anthropic', in:1.20,  out:4.50 },
  { upstream:'SkyAPI', model:'claude-sonnet-4-5',        canonical:'Claude Sonnet 4.5',family:'Anthropic', in:0.60,  out:3.00 },
  { upstream:'SkyAPI', model:'claude-haiku-4-5',         canonical:'Claude Haiku 4.5', family:'Anthropic', in:0.20,  out:1.00 },

  // ===== SuperToken - Max (官方源) =====
  { upstream:'SuperToken - Max', model:'claude-opus-4-7',  canonical:'Claude Opus 4.7',  family:'Anthropic', in:2.00,  out:10.00 },
  { upstream:'SuperToken - Max', model:'claude-opus-4-6',  canonical:'Claude Opus 4.6',  family:'Anthropic', in:2.00,  out:10.00 },
  { upstream:'SuperToken - Max', model:'claude-sonnet-4-6',canonical:'Claude Sonnet 4.6',family:'Anthropic', in:1.18,  out:5.90 },
  { upstream:'SuperToken - Max', model:'claude-haiku-4-5', canonical:'Claude Haiku 4.5', family:'Anthropic', in:0.40,  out:2.00 },

  // ===== SuperToken - 逆向渠道 (低价) =====
  { upstream:'SuperToken - 逆向', model:'claude-opus-4-7',   canonical:'Claude Opus 4.7',  family:'Anthropic', in:0.65,  out:3.30 },
  { upstream:'SuperToken - 逆向', model:'claude-opus-4-6',   canonical:'Claude Opus 4.6',  family:'Anthropic', in:0.65,  out:3.30 },
  { upstream:'SuperToken - 逆向', model:'claude-sonnet-4-6', canonical:'Claude Sonnet 4.6',family:'Anthropic', in:0.39,  out:1.95 },
  { upstream:'SuperToken - 逆向', model:'claude-haiku-4-5',  canonical:'Claude Haiku 4.5', family:'Anthropic', in:0.13,  out:0.65 },

  // ===== OpenRouter (暂时隐藏) =====
  // 数据保留以便后续放开；当前不展示在定价表 + 上游池 + 过滤器中。
  // 注释整块即可，恢复时取消注释。
  // { upstream:'OpenRouter', model:'openai/gpt-5.2',                 canonical:'GPT-5.2',           family:'OpenAI',    in:1.75,  out:14.00 },
  // { upstream:'OpenRouter', model:'openai/gpt-5-mini',              canonical:'GPT-5 mini',        family:'OpenAI',    in:0.25,  out:2.00 },
  // { upstream:'OpenRouter', model:'openai/gpt-5-nano',              canonical:'GPT-5 nano',        family:'OpenAI',    in:0.05,  out:0.40 },
  // { upstream:'OpenRouter', model:'openai/gpt-4o-mini',             canonical:'GPT-4o mini',       family:'OpenAI',    in:0.15,  out:0.60 },
  // { upstream:'OpenRouter', model:'anthropic/claude-opus-4.6',      canonical:'Claude Opus 4.6',   family:'Anthropic', in:10.00, out:37.50 },
  // { upstream:'OpenRouter', model:'anthropic/claude-opus-4.5',      canonical:'Claude Opus 4.5',   family:'Anthropic', in:5.00,  out:25.00 },
  // { upstream:'OpenRouter', model:'anthropic/claude-sonnet-4.5',    canonical:'Claude Sonnet 4.5', family:'Anthropic', in:6.00,  out:22.50 },
  // { upstream:'OpenRouter', model:'anthropic/claude-haiku-4.5',     canonical:'Claude Haiku 4.5',  family:'Anthropic', in:1.00,  out:5.00 },
  // { upstream:'OpenRouter', model:'google/gemini-3-pro-preview',    canonical:'Gemini 3 Pro',         family:'Google',    in:4.00,  out:18.00 },
  // { upstream:'OpenRouter', model:'google/gemini-3-flash-preview',  canonical:'Gemini 3 Flash',       family:'Google',    in:0.50,  out:3.00 },
  // { upstream:'OpenRouter', model:'google/gemini-2.5-pro',          canonical:'Gemini 2.5 Pro',       family:'Google',    in:2.50,  out:15.00 },
  // { upstream:'OpenRouter', model:'google/gemini-2.5-flash',        canonical:'Gemini 2.5 Flash',     family:'Google',    in:0.30,  out:2.50 },
  // { upstream:'OpenRouter', model:'google/gemini-2.5-flash-lite',   canonical:'Gemini 2.5 Flash-Lite',family:'Google',    in:0.10,  out:0.40 },
  // { upstream:'OpenRouter', model:'x-ai/grok-4-fast',               canonical:'Grok 4 Fast',       family:'xAI',       in:0.40,  out:1.00 },
  // { upstream:'OpenRouter', model:'x-ai/grok-4.1-fast',             canonical:'Grok 4.1 Fast',     family:'xAI',       in:0.40,  out:1.00 },
  // { upstream:'OpenRouter', model:'x-ai/grok-code-fast-1',          canonical:'Grok Code Fast',    family:'xAI',       in:0.20,  out:1.50 },
  // { upstream:'OpenRouter', model:'deepseek/deepseek-r1',           canonical:'DeepSeek R1',       family:'DeepSeek',  in:0.70,  out:2.50 },
  // { upstream:'OpenRouter', model:'deepseek/deepseek-chat',         canonical:'DeepSeek Chat',     family:'DeepSeek',  in:0.30,  out:1.20 },
  // { upstream:'OpenRouter', model:'deepseek/deepseek-v3.2',         canonical:'DeepSeek V3.2',     family:'DeepSeek',  in:0.25,  out:0.38 },
  // { upstream:'OpenRouter', model:'z-ai/glm-4.7',                   canonical:'GLM 4.7',           family:'智谱 GLM',   in:0.40,  out:1.50 },
  // { upstream:'OpenRouter', model:'minimax/minimax-m2.1',           canonical:'MiniMax M2.1',      family:'MiniMax',   in:0.27,  out:0.95 },
  // { upstream:'OpenRouter', model:'moonshotai/kimi-k2.5',           canonical:'Kimi K2.5',         family:'Moonshot',  in:0.45,  out:2.25 },
  // { upstream:'OpenRouter', model:'qwen/qwen3-embedding-8b',        canonical:'Qwen3 Embedding 8B',family:'阿里 Qwen', in:0.40,  out:1.50 },

  // ===== BLT (图像生成) =====
  { upstream:'BLT', model:'gpt-image-2', canonical:'GPT Image 2', family:'OpenAI 图像', in:0.006, out:0.032 },
];

export const XData = {
  CATEGORIES, APIS, sparkSeries, RECENT_CALLS, API_KEYS, MY_APIS, PENDING_REVIEWS,
  // AI_MODEL_PRICING 已废弃 — /ai 页面现从 GET /api/models 实时取数。
  // 历史数据保留在下方常量中（未导出），后续可彻底删除。
};

// ============ Environment Variables store ============
// Client-side only: server never sees the values. Stored in localStorage.
// Usage:
//   XEnv.get(name)         -> value string | undefined
//   XEnv.getAll()          -> [{name, value, scope, description, createdAt}]
//   XEnv.set({name, value, scope?, description?})
//   XEnv.remove(name)
//   XEnv.subscribe(fn)     -> unsub
//   XEnv.resolve(str)      -> replaces ${VAR} tokens with values, leaves ${VAR} if unset
const ENV_KEY_BASE = 'xapi.envVars';
// User-scoped so switching logged-in user gives a clean env.
// Shell writes window.__xapi_user_handle on auth init.
const getUserHandle = () => {
  try {
    return window.__xapi_user_handle ||
           JSON.parse(localStorage.getItem('xapi.currentUser') || 'null')?.handle ||
           'guest';
  } catch { return 'guest'; }
};
const envKey = () => ENV_KEY_BASE + ':' + getUserHandle();
const ENV_SEED = [
  // Seed with canonical ones so UI demonstrates state; user can still delete/edit.
  { name: 'XAPI_KEY', value: '', scope: 'global',
    description: 'Your xapi gateway key. Used by MCP config and Try-It. Never sent to xapi servers.',
    required: true, createdAt: Date.now() },
];
const envListeners = new Set();
const envLoad = () => {
  try {
    const raw = localStorage.getItem(envKey());
    if (!raw) return ENV_SEED.slice();
    const parsed = JSON.parse(raw);
    // Make sure seeded required ones are always present.
    const names = new Set(parsed.map(v => v.name));
    ENV_SEED.forEach(s => { if (!names.has(s.name)) parsed.unshift(s); });
    return parsed;
  } catch { return ENV_SEED.slice(); }
};
const envSave = (vars) => {
  localStorage.setItem(envKey(), JSON.stringify(vars));
  envListeners.forEach(fn => { try { fn(vars); } catch {} });
};
export const XEnv = {
  getAll: () => envLoad(),
  _notify: () => { const vars = envLoad(); envListeners.forEach(fn => { try { fn(vars); } catch {} }); },
  get: (name) => {
    const v = envLoad().find(x => x.name === name);
    return v && v.value ? v.value : undefined;
  },
  set: ({ name, value, scope = 'global', description = '' }) => {
    const vars = envLoad();
    const i = vars.findIndex(v => v.name === name);
    if (i >= 0) vars[i] = { ...vars[i], value, scope, description };
    else vars.push({ name, value, scope, description, createdAt: Date.now() });
    envSave(vars);
  },
  // Auto-set on apikey login flows: don't clobber a value the user already chose
  // (e.g. their preferred sk-1e77 set during a Twitter session). Only writes when empty.
  setIfEmpty: ({ name, value, scope = 'global', description = '' }) => {
    const existing = XEnv.get(name);
    if (existing) return false;
    XEnv.set({ name, value, scope, description });
    return true;
  },
  remove: (name) => {
    const vars = envLoad().filter(v => v.name !== name || v.required);
    envSave(vars);
  },
  subscribe: (fn) => { envListeners.add(fn); return () => envListeners.delete(fn); },
  resolve: (str) => {
    if (!str) return str;
    const vars = envLoad();
    const map = Object.fromEntries(vars.map(v => [v.name, v.value]));
    return str.replace(/\$\{([A-Z0-9_]+)\}/g, (m, n) => map[n] || m);
  },
  useEnv: () => {
    const [vars, setVars] = React.useState(envLoad());
    React.useEffect(() => {
      const unsub = XEnv.subscribe(setVars);
      return unsub;
    }, []);
    return vars;
  },
};

// Legacy global access for any consumer that still reaches for window.XEnv
// (e.g. inline event handlers, or 3rd-party scripts). Safe to remove later.
if (typeof window !== 'undefined') { window.XEnv = XEnv; window.XData = XData; }
